// ── useSpeechToText — push-to-talk (Fase 4) ──────────────────────────────────
// Graba con MediaRecorder nativo, sube a POST /api/assistant/transcribe
// (server llama Groq). Nunca auto-envía: el caller decide qué hacer con el
// texto transcripto (AssistantShell lo pone en el input para que se pueda
// corregir antes de mandar).

import { useRef, useState, useCallback } from 'react';

export interface UseSpeechToTextResult {
  isRecording: boolean;
  isTranscribing: boolean;
  error: string | null;
  startRecording: () => void;
  stopRecording: () => void;
}

export function useSpeechToText(onTranscribed: (text: string) => void): UseSpeechToTextResult {
  const [isRecording, setIsRecording]         = useState(false);
  const [isTranscribing, setIsTranscribing]   = useState(false);
  const [error, setError]                     = useState<string | null>(null);

  const mediaRecorderRef = useRef<MediaRecorder | null>(null);
  const chunksRef        = useRef<Blob[]>([]);
  const streamRef        = useRef<MediaStream | null>(null);

  const startRecording = useCallback(() => {
    if (mediaRecorderRef.current) return; // ya grabando
    setError(null);

    navigator.mediaDevices.getUserMedia({ audio: true })
      .then(stream => {
        streamRef.current = stream;
        const mimeType = MediaRecorder.isTypeSupported('audio/webm') ? 'audio/webm' : '';
        const recorder = mimeType ? new MediaRecorder(stream, { mimeType }) : new MediaRecorder(stream);
        chunksRef.current = [];

        recorder.ondataavailable = e => {
          if (e.data.size > 0) chunksRef.current.push(e.data);
        };

        recorder.onstop = () => {
          streamRef.current?.getTracks().forEach(track => track.stop());
          streamRef.current = null;
          const blob = new Blob(chunksRef.current, { type: recorder.mimeType || 'audio/webm' });
          chunksRef.current = [];
          mediaRecorderRef.current = null;
          setIsRecording(false);

          if (blob.size === 0) return;

          setIsTranscribing(true);
          const form = new FormData();
          const ext = blob.type.includes('webm') ? 'webm'
            : blob.type.includes('ogg') ? 'ogg'
            : blob.type.includes('mp4') ? 'mp4'
            : blob.type.includes('mpeg') ? 'mp3'
            : 'wav'; // Safari sin mimeType explícito suele caer acá — MediaRecorder default
          form.append('audio', blob, `voice.${ext}`);

          fetch('/api/assistant/transcribe', {
            method: 'POST',
            headers: { 'X-Requested-With': 'XMLHttpRequest' },
            body: form,
          })
            .then(async r => {
              const data = await r.json().catch(() => ({})) as { text?: string; error?: string };
              if (!r.ok) throw new Error(data.error ?? `HTTP ${r.status}`);
              return data;
            })
            .then(data => { if (data.text) onTranscribed(data.text); })
            .catch((err: unknown) => setError(err instanceof Error ? err.message : String(err)))
            .finally(() => setIsTranscribing(false));
        };

        mediaRecorderRef.current = recorder;
        recorder.start();
        setIsRecording(true);
      })
      .catch((err: unknown) => {
        setError(err instanceof Error ? err.message : String(err));
      });
  }, [onTranscribed]);

  const stopRecording = useCallback(() => {
    mediaRecorderRef.current?.stop();
  }, []);

  return { isRecording, isTranscribing, error, startRecording, stopRecording };
}
